import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useTranslation, useLocaleContext, LOCALE_CONFIG, type SupportedLocale } from '@parentpath/i18n';
import { cn } from '@/lib/utils';

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const localeOptions = (Object.keys(LOCALE_CONFIG) as SupportedLocale[]).map((code) => ({
  code,
  nativeName: LOCALE_CONFIG[code].nativeName,
}));

export function LanguageModal({ isOpen, onClose }: LanguageModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const { locale, setLocale } = useLocaleContext();
  const [selected, setSelected] = useState<SupportedLocale>(locale);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(locale);
    }
  }, [isOpen, locale]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (selected === locale) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await setLocale(selected);
      onClose();
    } catch (err) {
      console.error('Error saving language:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{t('profile:language.title')}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('profile:language.description')}
            </p>

            <div className="space-y-3">
              {localeOptions.map((option) => (
                <button
                  key={option.code}
                  onClick={() => setSelected(option.code)}
                  className={cn(
                    'w-full p-4 rounded-[15px] border text-left transition-all',
                    selected === option.code
                      ? 'bg-pp-orange/20 border-pp-orange'
                      : 'bg-white/10 border-white/20 hover:bg-white/15'
                  )}
                >
                  <p className="font-medium text-white">{option.nativeName}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('common:actions.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
